import type { ChipId } from './types'

export type ChipStatus =
  | 'available'
  | 'active'
  | 'used'
  | 'locked'
  | 'ineligible'
  | 'cooldown'
  | 'comingSoon'
  | 'deadlineLocked'

// The gameweek a 'used' chip was played, plus Goalfest/Clean Sweep's frozen
// record number — undefined for chips with no
// record, which renders the played line with just the gameweek.
export interface ChipUsedDetail {
  gameweek: number
  suffix?: string
}

export interface ChipRuntimeConfig {
  usedGameweeks: number[]
  seasonCap: number
  isEligible: boolean
  hasCooldown: boolean
  // After the first use, the second use is locked until this gameweek. Every chip sets it.
  secondUseUnlockGameweek?: number
  // A chip-specific gameweek before which it can't be played at all, regardless of
  // eligibility (e.g. the old Underdog Boost's GW12 gate). Unlike secondUseUnlockGameweek,
  // this applies before any use has been made. Currently unused by any active chip, but
  // structurally reusable by a future one.
  unlockGameweek?: number
  // Whether this gameweek's deadline has already passed — freezes the chip's current
  // state (played or not) so it can no longer be toggled.
  deadlinePassed?: boolean
}

function isActive(config: ChipRuntimeConfig | undefined, gameweek: number): boolean {
  return config?.usedGameweeks.includes(gameweek) ?? false
}

export function getChipStatus(
  chipId: ChipId,
  implemented: boolean,
  gameweek: number,
  configs: Partial<Record<ChipId, ChipRuntimeConfig>>
): ChipStatus {
  if (!implemented) return 'comingSoon'

  const config = configs[chipId]
  if (!config) return 'comingSoon'

  if (config.usedGameweeks.includes(gameweek)) return 'active'

  // Once a gameweek's deadline has passed, its chip state is frozen — this must
  // short-circuit before eligibility/cooldown/cap/mutual-exclusion logic, none of
  // which should be able to reopen a chip for a gameweek that's already locked in.
  if (config.deadlinePassed) return 'deadlineLocked'

  if (config.unlockGameweek !== undefined && gameweek < config.unlockGameweek) {
    return 'locked'
  }

  // No two chips may be active in the same gameweek, regardless of scoring mechanism.
  // Banker is exempt — it's a per-game pick, not a chip.
  const blockedByOtherChip = (Object.keys(configs) as ChipId[]).some(
    (otherId) => otherId !== chipId && isActive(configs[otherId], gameweek)
  )
  if (blockedByOtherChip) return 'locked'

  if (config.usedGameweeks.length >= config.seasonCap) return 'used'
  if (!config.isEligible) return 'ineligible'
  if (config.hasCooldown && config.usedGameweeks.includes(gameweek - 1)) return 'cooldown'
  if (
    config.secondUseUnlockGameweek !== undefined &&
    config.usedGameweeks.length === 1 &&
    gameweek < config.secondUseUnlockGameweek
  ) {
    return 'locked'
  }

  return 'available'
}

/** Adds `gameweek` to a chip's used-gameweeks list, unless its deadline has already passed. */
export function playChip(
  usedGameweeks: number[],
  gameweek: number,
  deadlinePassed: boolean
): number[] {
  if (deadlinePassed) return usedGameweeks
  if (usedGameweeks.includes(gameweek)) return usedGameweeks
  return [...usedGameweeks, gameweek]
}

/** Removes `gameweek` from a chip's used-gameweeks list, unless its deadline has already passed. */
export function cancelChip(
  usedGameweeks: number[],
  gameweek: number,
  deadlinePassed: boolean
): number[] {
  if (deadlinePassed) return usedGameweeks
  return usedGameweeks.filter((gw) => gw !== gameweek)
}
